import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Badge, Btn } from "@/components/ui-kit";
import { ShieldCheck, Lock, Save, RotateCcw } from "lucide-react";

function Toggle({ checked, disabled, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
        checked ? "bg-primary" : "bg-muted"
      } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

export default function Permissions() {
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [matrix, setMatrix] = useState({}); // role -> {key: bool}
  const [dirty, setDirty] = useState({}); // role -> true
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/permissions")
      .then((r) => {
        setGroups(r.data.groups);
        setRoles(r.data.roles);
        const m = {};
        r.data.roles.forEach((role) => {
          m[role.role] = { ...role.permissions };
        });
        setMatrix(m);
        setDirty({});
      })
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const editableRoles = useMemo(() => roles.filter((r) => !r.locked), [roles]);

  const toggle = (role, key, val) => {
    setMatrix((prev) => ({ ...prev, [role]: { ...prev[role], [key]: val } }));
    setDirty((prev) => ({ ...prev, [role]: true }));
  };

  const dirtyRoles = Object.keys(dirty).filter((r) => dirty[r]);

  const save = async () => {
    if (dirtyRoles.length === 0) {
      toast.info("No changes to save");
      return;
    }
    setSaving(true);
    try {
      for (const role of dirtyRoles) {
        await api.put(`/permissions/${role}`, { permissions: matrix[role] });
      }
      toast.success("Permissions updated");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading permissions…</div>;

  return (
    <div className="space-y-4" data-testid="permissions-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold uppercase tracking-tight sm:text-3xl">
            <ShieldCheck size={24} /> Roles &amp; Permissions
          </h1>
          <p className="text-sm text-muted-foreground">
            Configure exactly which pages and actions each role can access. The Manager always has full access.
          </p>
        </div>
        <div className="flex gap-2">
          <Btn variant="outline" onClick={load} disabled={saving} data-testid="perm-reset">
            <RotateCcw size={15} /> Reset
          </Btn>
          <Btn onClick={save} disabled={saving || dirtyRoles.length === 0} data-testid="perm-save">
            <Save size={15} /> {saving ? "Saving…" : `Save${dirtyRoles.length ? ` (${dirtyRoles.length})` : ""}`}
          </Btn>
        </div>
      </div>

      <Card className="overflow-x-auto p-0">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left">
              <th className="sticky left-0 z-10 bg-muted/40 px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                Permission
              </th>
              {roles.map((role) => (
                <th key={role.role} className="px-4 py-3 text-center">
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="font-heading text-xs font-bold uppercase tracking-tight">{role.label}</span>
                    {role.locked && (
                      <Badge className="border-primary/40 text-primary">
                        <Lock size={10} className="mr-1" /> Full
                      </Badge>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <React.Fragment key={group.group}>
                <tr className="border-b border-border/60 bg-accent/30">
                  <td
                    colSpan={roles.length + 1}
                    className="px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground"
                  >
                    {group.group}
                  </td>
                </tr>
                {group.perms.map((perm) => (
                  <tr key={perm.key} className="border-b border-border/40 hover:bg-accent/20">
                    <td className="sticky left-0 z-10 bg-card px-4 py-2.5 font-medium">{perm.label}</td>
                    {roles.map((role) => {
                      const checked = !!(matrix[role.role] && matrix[role.role][perm.key]);
                      return (
                        <td key={role.role} className="px-4 py-2.5 text-center">
                          <div className="flex justify-center">
                            <Toggle
                              checked={role.locked ? true : checked}
                              disabled={role.locked}
                              onChange={(v) => toggle(role.role, perm.key, v)}
                            />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </Card>

      <p className="text-xs text-muted-foreground">
        Changes apply to a role the next time those users load the app. Turning off a page hides it from the sidebar and
        blocks its actions on the server.
      </p>
      {editableRoles.length === 0 && (
        <p className="text-sm text-muted-foreground">No editable roles found.</p>
      )}
    </div>
  );
}
